import styled from "styled-components";
import { TypoCaptionStyles, TypoFine2 } from "@/styles/Styled.Typography";
import { fluid } from "@/styles/utils/fluid";

export const ArticleContent = styled.div`
  sup {
    font-size: ${({ theme }) =>
      fluid({
        minSize: theme.fontScale.majorSecond.caption,
        maxSize: theme.fontScale.augmentedFourth.caption,
        minVW: theme.breakpointValues.xs,
        maxVW: theme.breakpointValues.xl,
        baseFontMin: theme.baseFont.xs,
        baseFontMax: theme.baseFont.xl,
      })};
    vertical-align: super;
    line-height: 0;
    margin-left: ${({ theme }) => theme.spacing.vertical.xs / 2}rem;

    a {
      text-decoration: none;
      color: ${({ theme }) => theme.colors.imperialRed};
    }
  }
  .footnotes {
    h2 {
      > div {
        font-size: ${({ theme }) =>
          fluid({
            minSize: theme.fontScale.majorSecond.label,
            maxSize: theme.fontScale.augmentedFourth.label,
            minVW: theme.breakpointValues.xs,
            maxVW: theme.breakpointValues.xl,
            baseFontMin: theme.baseFont.xs,
            baseFontMax: theme.baseFont.xl,
          })};
        padding-top: ${({ theme }) => `
          calc(${fluid({
            minSize: theme.spacing.section.xs,
            maxSize: theme.spacing.section.xl,
            minVW: theme.breakpointValues.xs,
            maxVW: theme.breakpointValues.xl,
            baseFontMin: theme.baseFont.xs,
            baseFontMax: theme.baseFont.xl,
          })} * 1.5)
        `};
        padding-bottom: 0;
        position: relative;
        &::before {
          content: "";
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
          width: 100px;
          height: 4px;
          border: 0;
          border-top: 1px solid ${({ theme }) => theme.colors.phillipineSilver};
          border-bottom: 1px solid
            ${({ theme }) => theme.colors.phillipineSilver};
          margin: 0 auto;
        }
      }
    }
    ul,
    ol {
      ${TypoCaptionStyles};
      font-size: ${({ theme }) =>
        fluid({
          minSize: theme.fontScale.majorSecond.caption,
          maxSize: theme.fontScale.augmentedFourth.caption,
          minVW: theme.breakpointValues.xs,
          maxVW: theme.breakpointValues.xl,
          baseFontMin: theme.baseFont.xs,
          baseFontMax: theme.baseFont.xl,
        })};
      &::before,
      &::after {
        border-top: 0;
      }
      li {
        p {
          ${TypoCaptionStyles};
          font-size: ${({ theme }) =>
            fluid({
              minSize: theme.fontScale.majorSecond.caption,
              maxSize: theme.fontScale.augmentedFourth.caption,
              minVW: theme.breakpointValues.xs,
              maxVW: theme.breakpointValues.xl,
              baseFontMin: theme.baseFont.xs,
              baseFontMax: theme.baseFont.xl,
            })};
        }
        a {
          display: inline;
          padding-bottom: 4px; /* space for underline */
          text-decoration: none;
          white-space: normal;
          color: ${({ theme }) => theme.colors.imperialRed};
          background-image: linear-gradient(
            to right,
            ${({ theme }) => theme.colors.imperialRed},
            ${({ theme }) => theme.colors.imperialRed}
          );
          background-size: 0% 2px; /* start with 0 width, 2px tall */
          background-repeat: no-repeat;
          background-position: left bottom; /* stick it to the baseline */
          transition: background-size 0.3s ease;
          &:hover {
            background-size: 100% 2px; /* animate width across the text */
          }
        }
      }
    }
  }
`;

export const GridContentWrapper = styled.div`
  display: grid;
  gap: 0;
  min-height: 0;
  min-width: 0; /* NEW; needed for Firefox */
  &:last-of-type {
    border-bottom: none;
  }
  grid-template-rows: min-content;
  /* xs only */
  @media ${({ theme }) => theme.breakpoints.xs} {
    grid-template-columns: 1fr;
  }
  /* sm and up */
  @media ${({ theme }) => theme.breakpoints.sm} {
    grid-template-columns: 1fr;
  }
  /* sm to md */
  @media ${({ theme }) => theme.breakpoints.md} {
    grid-template-columns: [outer-start] 1fr [center-start] 8fr [center-end] 1fr [outer-end];
    grid-template-rows: min-content 1fr min-content;
  }
  /* md and up */
  @media ${({ theme }) => theme.breakpoints.lg} {
    grid-template-columns: 1fr 2fr;
  }
  /* lg to xl */
  @media ${({ theme }) => theme.breakpoints.xl} {
    grid-template-columns: [outer-start] 1fr [center-start] 3fr [center-end] 1.5fr [outer-end];
  }
`;

export const GridItemContent = styled.div`
  /* xs only */
  @media ${({ theme }) => theme.breakpoints.xs} {
    &:nth-child(1),
    &:nth-child(2) {
      border-top: 1px solid ${({ theme }) => theme.colors.phillipineSilver};
    }
    &:nth-child(1),
    &:nth-child(4) {
      padding: ${({ theme }) => theme.spacing.section.xs}rem 0;
    }
    &:nth-child(3),
    &:last-child {
      padding: 0 0 ${({ theme }) => theme.spacing.section.xs}rem;
    }
    &:nth-child(1) {
      order: 4;

      border-bottom: 1px solid ${({ theme }) => theme.colors.phillipineSilver};
    }
    &:nth-child(2) {
      order: 2;
      padding: ${({ theme }) => theme.spacing.section.xs}rem 0 0;
    }
    &:nth-child(3) {
      order: 3;
    }
    &:nth-child(4) {
      order: 5;
    }
    &:last-child {
      order: 1;
    }
  }
  /* sm and up */
  @media ${({ theme }) => theme.breakpoints.sm} {
    &:nth-child(1),
    &:nth-child(4) {
      padding: ${({ theme }) => `
        ${fluid({
          minSize: theme.spacing.section.xs,
          maxSize: theme.spacing.section.sm,
          minVW: theme.breakpointValues.xs,
          maxVW: theme.breakpointValues.sm,
          baseFontMin: theme.baseFont.xs,
          baseFontMax: theme.baseFont.sm,
        })} 
        0
      `};
    }
    &:nth-child(3),
    &:last-child {
      padding: ${({ theme }) => `
        0
        0
        ${fluid({
          minSize: theme.spacing.section.xs,
          maxSize: theme.spacing.section.sm,
          minVW: theme.breakpointValues.xs,
          maxVW: theme.breakpointValues.sm,
          baseFontMin: theme.baseFont.xs,
          baseFontMax: theme.baseFont.sm,
        })} 
      `};
    }
    &:nth-child(1) {
      order: 4;
      border-top: 1px solid ${({ theme }) => theme.colors.phillipineSilver};
      border-bottom: 1px solid ${({ theme }) => theme.colors.phillipineSilver};
    }
    &:nth-child(2) {
      order: 2;
      border-top: 1px solid ${({ theme }) => theme.colors.phillipineSilver};
      padding: ${({ theme }) => `
        ${fluid({
          minSize: theme.spacing.section.xs,
          maxSize: theme.spacing.section.sm,
          minVW: theme.breakpointValues.xs,
          maxVW: theme.breakpointValues.sm,
          baseFontMin: theme.baseFont.xs,
          baseFontMax: theme.baseFont.sm,
        })} 
        0
        0
      `};
    }
    &:nth-child(3) {
      order: 3;
    }
    &:nth-child(4) {
      order: 5;
    }
    &:last-child {
      order: 1;
    }
  }
  /* sm to md */
  @media ${({ theme }) => theme.breakpoints.md} {
    grid-column: center-start / center-end;
    &:nth-child(1),
    &:nth-child(4) {
      padding: ${({ theme }) => `
        ${fluid({
          minSize: theme.spacing.section.sm,
          maxSize: theme.spacing.section.md,
          minVW: theme.breakpointValues.sm,
          maxVW: theme.breakpointValues.md,
          baseFontMin: theme.baseFont.sm,
          baseFontMax: theme.baseFont.md,
        })} 
        0
      `};
    }
    &:nth-child(3),
    &:last-child {
      padding: ${({ theme }) => `
        0
        0
        ${fluid({
          minSize: theme.spacing.section.sm,
          maxSize: theme.spacing.section.md,
          minVW: theme.breakpointValues.sm,
          maxVW: theme.breakpointValues.md,
          baseFontMin: theme.baseFont.sm,
          baseFontMax: theme.baseFont.md,
        })} 
      `};
    }
    &:nth-child(1) {
      order: 4;
      border-top: 1px solid ${({ theme }) => theme.colors.phillipineSilver};
      border-bottom: 1px solid ${({ theme }) => theme.colors.phillipineSilver};
    }
    &:nth-child(2) {
      order: 2;
      border-top: 1px solid ${({ theme }) => theme.colors.phillipineSilver};
      padding: ${({ theme }) => `
        ${fluid({
          minSize: theme.spacing.section.sm,
          maxSize: theme.spacing.section.md,
          minVW: theme.breakpointValues.sm,
          maxVW: theme.breakpointValues.md,
          baseFontMin: theme.baseFont.sm,
          baseFontMax: theme.baseFont.md,
        })} 
        0
        0
      `};
    }
    &:nth-child(3) {
      order: 3;
    }
    &:nth-child(4) {
      order: 5;
    }
    &:last-child {
      order: 1;
    }
  }
  /* md and up */
  @media ${({ theme }) => theme.breakpoints.lg} {
    &:nth-child(1),
    &:nth-child(4) {
      padding: ${({ theme }) => `
        ${fluid({
          minSize: theme.spacing.section.md,
          maxSize: theme.spacing.section.lg,
          minVW: theme.breakpointValues.md,
          maxVW: theme.breakpointValues.lg,
          baseFontMin: theme.baseFont.md,
          baseFontMax: theme.baseFont.lg,
        })} 
        0
        ${fluid({
          minSize: theme.spacing.section.md,
          maxSize: theme.spacing.section.lg,
          minVW: theme.breakpointValues.md,
          maxVW: theme.breakpointValues.lg,
          baseFontMin: theme.baseFont.md,
          baseFontMax: theme.baseFont.lg,
        })} 
        ${fluid({
          minSize: theme.spacing.vertical.md,
          maxSize: theme.spacing.vertical.lg,
          minVW: theme.breakpointValues.md,
          maxVW: theme.breakpointValues.lg,
          baseFontMin: theme.baseFont.md,
          baseFontMax: theme.baseFont.lg,
        })} 
      `};
    }
    &:nth-child(1) {
      order: 4;
      grid-column: 2 / 3;
      border-left: 1px solid ${({ theme }) => theme.colors.phillipineSilver};
      border-top: 1px solid ${({ theme }) => theme.colors.phillipineSilver};
    }
    &:nth-child(2),
    &:nth-child(3),
    &:nth-child(4) {
      grid-column: 2 / 3;
      border-left: 1px solid ${({ theme }) => theme.colors.phillipineSilver};
    }
    &:nth-child(2),
    &:nth-child(3) {
      padding: ${({ theme }) => `
        0
        0
        0
        ${fluid({
          minSize: theme.spacing.vertical.md,
          maxSize: theme.spacing.vertical.lg,
          minVW: theme.breakpointValues.md,
          maxVW: theme.breakpointValues.lg,
          baseFontMin: theme.baseFont.md,
          baseFontMax: theme.baseFont.lg,
        })} 
      `};
    }
    &:nth-child(2) {
      order: 2;
    }
    &:nth-child(3) {
      order: 3;
      padding-bottom: ${({ theme }) =>
        fluid({
          minSize: theme.spacing.section.md,
          maxSize: theme.spacing.section.lg,
          minVW: theme.breakpointValues.md,
          maxVW: theme.breakpointValues.lg,
          baseFontMin: theme.baseFont.md,
          baseFontMax: theme.baseFont.lg,
        })};
    }
    &:nth-child(4) {
      order: 5;
      border-top: 1px solid ${({ theme }) => theme.colors.phillipineSilver};
    }
    &:last-child {
      order: 1;
      grid-row: 1 / span all;
      padding: ${({ theme }) => `
        0
        ${fluid({
          minSize: theme.spacing.vertical.md,
          maxSize: theme.spacing.vertical.lg,
          minVW: theme.breakpointValues.md,
          maxVW: theme.breakpointValues.lg,
          baseFontMin: theme.baseFont.md,
          baseFontMax: theme.baseFont.lg,
        })}
        0
        0
      `};
    }
  }
  /* lg to xl */
  @media ${({ theme }) => theme.breakpoints.xl} {
    border-bottom: none;
    &:nth-child(1) {
      order: 1;
      grid-column: 1 / 2;
      grid-row: 1 / span all;
      padding: ${({ theme }) => `
        0
        ${fluid({
          minSize: theme.spacing.vertical.lg,
          maxSize: theme.spacing.vertical.xl,
          minVW: theme.breakpointValues.lg,
          maxVW: theme.breakpointValues.xl,
          baseFontMin: theme.baseFont.lg,
          baseFontMax: theme.baseFont.xl,
        })} 
        0
        0
      `};
    }
    &:nth-child(2),
    &:nth-child(3),
    &:nth-child(4) {
      grid-column: 2 / 3;
      border-left: 1px solid ${({ theme }) => theme.colors.phillipineSilver};
      border-right: 1px solid ${({ theme }) => theme.colors.phillipineSilver};
    }
    &:nth-child(2),
    &:nth-child(3) {
      padding: ${({ theme }) => `
        0
        ${fluid({
          minSize: theme.spacing.vertical.lg,
          maxSize: theme.spacing.vertical.xl,
          minVW: theme.breakpointValues.lg,
          maxVW: theme.breakpointValues.xl,
          baseFontMin: theme.baseFont.lg,
          baseFontMax: theme.baseFont.xl,
        })} 
      `};
    }
    &:nth-child(2) {
      order: 2;
    }
    &:nth-child(3) {
      order: 3;
      padding-bottom: ${({ theme }) =>
        fluid({
          minSize: theme.spacing.section.lg,
          maxSize: theme.spacing.section.xl,
          minVW: theme.breakpointValues.lg,
          maxVW: theme.breakpointValues.xl,
          baseFontMin: theme.baseFont.lg,
          baseFontMax: theme.baseFont.xl,
        })};
    }
    &:nth-child(4) {
      order: 4;
      border-top: 1px solid ${({ theme }) => theme.colors.phillipineSilver};
      padding: ${({ theme }) => `
        ${fluid({
          minSize: theme.spacing.vertical.lg,
          maxSize: theme.spacing.vertical.xl,
          minVW: theme.breakpointValues.lg,
          maxVW: theme.breakpointValues.xl,
          baseFontMin: theme.baseFont.lg,
          baseFontMax: theme.baseFont.xl,
        })} 
        ${fluid({
          minSize: theme.spacing.vertical.lg,
          maxSize: theme.spacing.vertical.xl,
          minVW: theme.breakpointValues.lg,
          maxVW: theme.breakpointValues.xl,
          baseFontMin: theme.baseFont.lg,
          baseFontMax: theme.baseFont.xl,
        })} 
      `};
    }
    &:last-child {
      order: 5;
      grid-column: 3 / 4;
      grid-row: 1 / span all;
      padding: ${({ theme }) => `
        0
        0
        0
        ${fluid({
          minSize: theme.spacing.vertical.lg,
          maxSize: theme.spacing.vertical.xl,
          minVW: theme.breakpointValues.lg,
          maxVW: theme.breakpointValues.xl,
          baseFontMin: theme.baseFont.lg,
          baseFontMax: theme.baseFont.xl,
        })} 
      `};
    }
  }
`;

export const GridInfoWrapper = styled.div`
  display: grid;
  gap: 0;
  min-height: 0;
  min-width: 0; /* NEW; needed for Firefox */
  &:last-of-type {
    border-bottom: none;
  }
  gap: ${({ theme }) =>
    fluid({
      minSize: theme.spacing.vertical.xs,
      maxSize: theme.spacing.vertical.sm,
      minVW: theme.breakpointValues.xs,
      maxVW: theme.breakpointValues.xl,
      baseFontMin: theme.baseFont.xs,
      baseFontMax: theme.baseFont.xl,
    })};
  /* xs only */
  @media ${({ theme }) => theme.breakpoints.xs} {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: min-content min-content min-content 1fr;
  }
  /* sm and up */
  @media ${({ theme }) => theme.breakpoints.sm} {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: min-content min-content min-content 1fr;
  }
  /* sm to md */
  @media ${({ theme }) => theme.breakpoints.md} {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: min-content min-content min-content 1fr;
  }
  /* md and up */
  @media ${({ theme }) => theme.breakpoints.lg} {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: min-content min-content min-content 1fr;
    order: 2;
    grid-row: span 2;
    padding: 0;
  }
  /* lg to xl */
  @media ${({ theme }) => theme.breakpoints.xl} {
    grid-template-columns: 1fr 1fr;
    grid-auto-rows: min-content;
  }
`;

export const GridItemInfo = styled.div`
  &:nth-child(1) {
    grid-column: 1 / 2;
    justify-self: start;
  }
  &:nth-child(2) {
    grid-column: 2 / 3;
    justify-self: end;
  }
  /* xs only */
  @media ${({ theme }) => theme.breakpoints.xs} {
    &:nth-of-type(5) {
      justify-self: center;
    }
    &:nth-of-type(3),
    &:nth-of-type(4),
    &:nth-of-type(5) {
      grid-column: span 2;
    }
  }
  /* sm and up */
  @media ${({ theme }) => theme.breakpoints.sm},
    /* sm to md */ ${({ theme }) => theme.breakpoints.md},
    /* md and up */ ${({ theme }) => theme.breakpoints.lg},
    /* lg to xl */ ${({ theme }) => theme.breakpoints.xl} {
    &:nth-child(3),
    &:nth-child(4) {
      grid-column: 1 / 3;
    }
  }
`;

export const FlexInfoWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: ${({ theme }) =>
    fluid({
      minSize: theme.spacing.vertical.xs,
      maxSize: theme.spacing.vertical.sm,
      minVW: theme.breakpointValues.xs,
      maxVW: theme.breakpointValues.xl,
      baseFontMin: theme.baseFont.xs,
      baseFontMax: theme.baseFont.xl,
    })};
  /* xs only */
  @media ${({ theme }) => theme.breakpoints.xs} {
    flex-direction: auto;
  }
`;

export const ChipLabel = styled(TypoFine2)`
  background-color: ${({ theme }) => theme.colors.platinum};
  padding: 8px;
  border-radius: 8px;
  height: min-content;
  width: min-content;
  text-transform: capitalize;
  white-space: nowrap;
`;
